import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { 
  Search, 
  Moon, 
  Sun, 
  LayoutGrid,
  ArrowRight,
  User,
  Layers,
  Filter,
  List,
  Square,
  Eye,
  Users,
  X,
  Pencil,
  Trash2,
  MoreHorizontal,
  MoreVertical,
  RefreshCw,
  Calendar,
  CheckSquare,
  Paperclip,
  Plus,
  Star,
  CheckCircle,
  Clock
} from 'lucide-react';

import { API } from '../config';
import { API_BASE_URL } from '../config';
import { ResponsiveLogo } from '../components/ThemeLogo';

const API_BASE = API_BASE_URL;

// ─── Due-date color helper (same as BoardPage) ───
const getDueDateClass = (dueDate) => {
  if (!dueDate) return '';
  const date = new Date(dueDate);
  const today = new Date();
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (dateOnly.getTime() === todayOnly.getTime()) return 'bg-orange-500/20 text-orange-600';
  if (dateOnly.getTime() < todayOnly.getTime()) return 'bg-red-500/20 text-red-600';
  return 'bg-muted text-muted-foreground';
};

// ─── Read-only Card ───
function PreviewCard({ card }) {
  return (
    <div className="mb-2" data-testid={`preview-card-${card.card_id}`}>
      {/* Status banners */}
      {card.status === 'completed' && (
        <div className="flex items-center gap-1.5 px-2 py-1 bg-green-100 text-green-700 text-[11px] font-semibold rounded-t-lg border border-b-0 border-green-300">
          <CheckCircle className="w-3 h-3 text-green-600" /> Completed
        </div>
      )}
      {card.status === 'in_progress' && (
        <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50 text-orange-600 text-[11px] font-semibold rounded-t-lg border border-b-0 border-orange-300">
          <Clock className="w-3 h-3" /> In Progress
        </div>
      )}

      <div className={`p-2.5 bg-background border border-border shadow-sm ${
        card.status === 'completed' || card.status === 'in_progress' ? 'rounded-b-lg' : 'rounded-lg'
      }`}>
        {/* Header row: icon + title + flip + menu (visual only) */}
        <div className="flex items-center gap-2 min-h-[32px]">
          <div className="shrink-0 w-8 h-8 rounded-full bg-white dark:bg-zinc-800 shadow-sm flex items-center justify-center">
            <img src={card.card_icon || '/icons/default-question.svg'} alt="" className="w-5 h-5 object-contain" />
          </div>
          <span className="flex-1 text-sm font-medium text-card-foreground min-w-0 line-clamp-2 break-words">{card.title}</span>
          <span className="shrink-0 p-1 rounded text-muted-foreground">
            <RefreshCw className="w-3.5 h-3.5" />
          </span>
          <span className="shrink-0 p-1 rounded text-muted-foreground">
            <MoreVertical className="w-3.5 h-3.5" />
          </span>
        </div>

        {/* Labels */}
        {card.labels?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {card.labels.map((label, idx) => (
              <span key={idx} className="h-1.5 w-7 rounded-full" style={{ backgroundColor: label.color || label }} title={label.name || ''} />
            ))}
          </div>
        )}

        {/* Bottom metadata row */}
        {(card.priority || card.due_date || card.checklist?.length > 0 || card.attachments?.length > 0 || card.assigned_members?.length > 0) && (
          <div className="flex items-center gap-1.5 mt-1.5 flex-nowrap overflow-hidden">
            {card.priority && (
              <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                card.priority === 'urgent' ? 'bg-red-500/20 text-red-600' :
                card.priority === 'high' ? 'bg-orange-500/20 text-orange-600' :
                card.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-600' :
                'bg-green-500/20 text-green-600'
              }`}>
                {card.priority.charAt(0).toUpperCase() + card.priority.slice(1)}
              </span>
            )}
            {card.due_date && (
              <span className={`shrink-0 flex items-center gap-0.5 text-[10px] px-1 py-0.5 rounded ${getDueDateClass(card.due_date)}`}>
                <Calendar className="w-2.5 h-2.5" />{new Date(card.due_date).toLocaleDateString()}
              </span>
            )}
            {card.checklist?.length > 0 && (
              <span className="shrink-0 flex items-center gap-0.5 text-[10px] text-muted-foreground">
                <CheckSquare className="w-2.5 h-2.5" />{card.checklist.filter(c => c.completed).length}/{card.checklist.length}
              </span>
            )}
            {card.attachments?.length > 0 && (
              <span className="shrink-0 flex items-center gap-0.5 text-[10px] text-muted-foreground">
                <Paperclip className="w-2.5 h-2.5" />{card.attachments.length}
              </span>
            )}
            {card.assigned_members?.length > 0 && (
              <div className="flex -space-x-1 ml-auto shrink-0">
                {card.assigned_members.slice(0, 2).map((m) => (
                  <div key={m.user_id} className="h-5 w-5 rounded-full border border-background bg-odapto-teal text-white text-[8px] flex items-center justify-center font-medium">
                    {m.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U'}
                  </div>
                ))}
                {card.assigned_members.length > 2 && (
                  <span className="flex items-center justify-center h-5 w-5 rounded-full bg-muted text-[8px] border border-background">
                    +{card.assigned_members.length - 2}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Read-only List Column ───
function PreviewList({ list, isNorthStar }) {
  return (
    <div
      className={`flex-shrink-0 w-full md:w-72 rounded-xl shadow-lg ${
        isNorthStar
          ? 'bg-gradient-to-b from-amber-50 to-card dark:from-amber-900/15 dark:to-card border-2 border-amber-300/40 dark:border-amber-600/30'
          : 'bg-card'
      }`}
      data-testid={`preview-list-${list.list_id}`}
    >
      {/* List Header */}
      <div className={`p-3 flex items-center justify-between border-b ${
        isNorthStar ? 'border-amber-200/60 dark:border-amber-700/30' : 'border-border'
      }`}>
        <div className="flex items-center gap-2 min-w-0">
          {isNorthStar && <Star className="w-4 h-4 text-amber-500 shrink-0" />}
          <h3 className={`font-semibold truncate ${
            isNorthStar ? 'text-amber-800 dark:text-amber-200' : 'text-card-foreground'
          }`}>
            {list.name}
          </h3>
          <span className={`text-xs px-1.5 py-0.5 rounded-full shrink-0 ${
            isNorthStar
              ? 'bg-amber-200/60 text-amber-700 dark:bg-amber-700/30 dark:text-amber-300'
              : 'bg-muted text-muted-foreground'
          }`}>
            {list.cards?.length || 0}
          </span>
        </div>
        <span className="p-1 shrink-0 ml-1 text-muted-foreground">
          <MoreHorizontal className="w-4 h-4" />
        </span>
      </div>

      {/* Cards area */}
      <div className="p-2 max-h-[calc(100vh-320px)] overflow-y-auto">
        {list.cards?.length > 0 ? (
          list.cards.map((card) => (
            <PreviewCard key={card.card_id} card={card} />
          ))
        ) : (
          <p className="text-xs text-muted-foreground text-center py-4">No cards</p>
        )}
      </div>

      {/* Add a card (visual only) */}
      <div className="p-2 border-t border-border">
        <span className="flex items-center gap-1 text-sm text-muted-foreground py-1.5 px-2">
          <Plus className="w-4 h-4" /> Add a card
        </span>
      </div>
    </div>
  );
}

// ─── Full Board Preview Container ───
function BoardPreview({ template, onClose, onUseTemplate }) {
  // Background style — same logic as BoardPage
  const backgroundStyle = template?.background_type === 'image' && template?.background
    ? {
        backgroundImage: `url(${API_BASE}${template.background})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    : { backgroundColor: template?.background || '#3A8B84' };

  return (
    <div className="flex flex-col h-full" data-testid="board-preview-container">
      {/* Board header bar — mimics the real board nav */}
      <nav className="shrink-0 bg-black/20 backdrop-blur-lg border-b border-white/10 z-10">
        <div className="px-4">
          <div className="flex items-center justify-between h-12">
            <div className="flex items-center gap-3 min-w-0">
              <span className="px-2 py-0.5 rounded bg-white/10 text-white text-[10px] font-bold uppercase tracking-wider">Preview</span>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="font-heading text-base sm:text-lg font-semibold text-white truncate">{template?.template_name || template?.name}</h1>
                  <Star className="w-4 h-4 text-amber-400 shrink-0" />
                </div>
                {template?.template_description && (
                  <p className="text-[11px] text-white/50 truncate max-w-md">{template.template_description}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Stats */}
              <div className="hidden sm:flex items-center gap-3 text-xs text-white/70 mr-2">
                <span className="flex items-center gap-1">
                  <List className="w-3.5 h-3.5" />
                  {template?.lists?.length || 0} lists
                </span>
                <span className="flex items-center gap-1">
                  <Square className="w-3.5 h-3.5" />
                  {template?.lists?.reduce((acc, l) => acc + (l.cards?.length || 0), 0) || 0} cards
                </span>
              </div>
              {template?.category && (
                <span className="hidden sm:inline text-[10px] bg-white/15 text-white/80 px-2 py-0.5 rounded-full">
                  {template.category.name}
                </span>
              )}
              <Button
                size="sm"
                onClick={onUseTemplate}
                className="bg-odapto-orange hover:bg-odapto-orange-hover text-white text-xs sm:text-sm"
                data-testid="preview-use-template-btn"
              >
                Use Template
                <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                data-testid="preview-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Board viewport with background */}
      <div className="flex-1 overflow-auto" style={backgroundStyle}>
        <div className="flex md:flex-row flex-col gap-4 items-start p-4 min-h-full">
          {template?.lists?.map((list, index) => (
            <PreviewList key={list.list_id} list={list} isNorthStar={index === 0} />
          ))}

          {/* Add another list (visual only) */}
          {template?.lists?.length > 0 && (
            <div className="flex-shrink-0 w-full md:w-72 rounded-xl bg-white/20 backdrop-blur-sm p-3">
              <span className="flex items-center gap-2 text-sm text-white/80 font-medium">
                <Plus className="w-4 h-4" /> Add another list
              </span>
            </div>
          )}

          {(!template?.lists || template.lists.length === 0) && (
            <div className="flex items-center justify-center w-full py-20">
              <div className="text-center text-white/70">
                <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-lg font-medium">Empty template</p>
                <p className="text-sm">This template has no lists yet.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom bar — creator info */}
      <div className="shrink-0 bg-black/30 backdrop-blur-lg border-t border-white/10 px-4 py-2">
        <div className="flex items-center justify-between text-xs text-white/60">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {template?.creator?.name || 'Unknown'}</span>
            <span className="hidden sm:inline">Read-only preview</span>
          </div>
          <div className="flex items-center gap-3 sm:hidden">
            <span className="flex items-center gap-1"><List className="w-3 h-3" />{template?.lists?.length || 0}</span>
            <span className="flex items-center gap-1"><Square className="w-3 h-3" />{template?.lists?.reduce((acc, l) => acc + (l.cards?.length || 0), 0) || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TemplatesPage() {
  const { user, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [workspaces, setWorkspaces] = useState([]);
  const [useDialogOpen, setUseDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [newBoardName, setNewBoardName] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Preview state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedCategory]);

  const fetchData = async () => {
    try {
      const [templatesRes, categoriesRes] = await Promise.all([
        fetch(`${API}/templates${selectedCategory !== 'all' ? `?category_id=${selectedCategory}` : ''}`),
        fetch(`${API}/template-categories`)
      ]);

      if (templatesRes.ok) {
        setTemplates(await templatesRes.json());
      }
      if (categoriesRes.ok) {
        setCategories(await categoriesRes.json());
      }

      // Fetch workspaces if authenticated
      if (isAuthenticated) {
        const wsRes = await apiGet('/workspaces');
        if (wsRes.ok) {
          setWorkspaces(await wsRes.json());
        }
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewTemplate = async (template) => {
    setPreviewLoading(true);
    setPreviewDialogOpen(true);
    try {
      const response = await fetch(`${API}/templates/${template.board_id}`);
      if (response.ok) {
        setPreviewTemplate(await response.json());
      } else {
        toast.error('Failed to load template preview');
        setPreviewDialogOpen(false);
      }
    } catch (error) {
      console.error('Failed to fetch template preview:', error);
      toast.error('Failed to load template preview');
      setPreviewDialogOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleUseTemplate = (template) => {
    if (!isAuthenticated) {
      toast.error('Please log in to use templates');
      navigate('/login');
      return;
    }

    setSelectedTemplate(template);
    setNewBoardName(template.template_name || '');
    setPreviewDialogOpen(false);
    setUseDialogOpen(true);
  };

  const canManageTemplate = (template) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return template.created_by === user.user_id;
  };

  const handleEditTemplate = (template) => {
    setEditTemplate(template);
    setEditName(template.template_name || '');
    setEditDesc(template.template_description || '');
    setEditCategory(template.template_category_id || '');
    setEditDialogOpen(true);
  };

  const saveTemplateEdit = async (e) => {
    e.preventDefault();
    if (!editName.trim()) return;
    setSaving(true);
    try {
      const response = await apiPut(`/templates/${editTemplate.board_id}`, {
        template_name: editName,
        template_description: editDesc,
        ...(editCategory ? { category_id: editCategory } : {})
      });
      if (response.ok) {
        toast.success('Template updated!');
        setEditDialogOpen(false);
        fetchData();
      } else {
        const err = response.headers.get('X-Error-Detail') || 'Failed to update template';
        toast.error(err);
      }
    } catch {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Delete template "${template.template_name}"? This cannot be undone.`)) return;
    try {
      const response = await apiDelete(`/templates/${template.board_id}`);
      if (response.ok) {
        toast.success('Template deleted');
        fetchData();
      } else {
        const err = response.headers.get('X-Error-Detail') || 'Failed to delete template';
        toast.error(err);
      }
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const useTemplate = async (e) => {
    e.preventDefault();
    if (!selectedWorkspace || !newBoardName.trim()) return;

    setCreating(true);
    try {
      const response = await apiPost(`/templates/${selectedTemplate.board_id}/use`, {
        workspace_id: selectedWorkspace,
        board_name: newBoardName
      });

      if (response.ok) {
        const board = await response.json();
        toast.success('Board created from template!');
        navigate(`/board/${board.board_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to use template');
      }
    } catch (error) {
      console.error('Failed to use template:', error);
      toast.error('Failed to use template');
    } finally {
      setCreating(false);
    }
  };

  const filteredTemplates = templates.filter(t =>
    t.template_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.template_description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="bg-background">
      {/* Header */}
      <div className="bg-gradient-to-br from-odapto-orange/10 via-background to-odapto-teal/10 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl sm:text-5xl font-bold mb-4"
          >
            Template Gallery
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-muted-foreground text-lg max-w-2xl mx-auto"
          >
            Jump-start your projects with ready-made board templates. 
            Created by the community, free to use.
          </motion.p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-templates-input"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.category_id} value={cat.category_id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-odapto-orange border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-muted flex items-center justify-center">
              <Layers className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-2">No templates found</h3>
            <p className="text-muted-foreground">
              {searchQuery ? 'Try a different search term' : 'No templates available yet'}
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template, index) => (
              <motion.div
                key={template.board_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group"
              >
                <div className="bg-card rounded-xl border border-border hover:border-odapto-orange/50 hover:shadow-lg transition-all overflow-hidden">
                  {/* Board-style Preview */}
                  <div 
                    className="relative cursor-pointer bg-muted/30 dark:bg-zinc-800/50"
                    onClick={() => handlePreviewTemplate(template)}
                    data-testid={`template-preview-trigger-${template.board_id}`}
                  >
                    {/* Mini board header */}
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-border/60">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: template.background || '#3A8B84' }} />
                      <span className="text-xs font-medium truncate">{template.template_name || template.name}</span>
                    </div>
                    {/* Mini board columns */}
                    <div className="flex gap-1.5 p-2.5 overflow-hidden h-[88px]">
                      {(template.lists || []).slice(0, 4).map((list, li) => (
                        <div key={li} className="flex-1 min-w-0 bg-muted/60 dark:bg-zinc-700/40 rounded-md p-1.5">
                          <div className="text-[9px] font-semibold text-muted-foreground truncate mb-1">{list.name}</div>
                          {Array.from({ length: Math.min(list.card_count || 2, 3) }).map((_, ci) => (
                            <div key={ci} className="bg-card dark:bg-zinc-600/50 rounded h-3.5 mb-0.5 shadow-[0_0.5px_1px_rgba(0,0,0,0.06)]" />
                          ))}
                        </div>
                      ))}
                      {(!template.lists || template.lists.length === 0) && (
                        <>
                          {['To Do', 'In Progress', 'Done'].map((name, li) => (
                            <div key={li} className="flex-1 min-w-0 bg-muted/60 dark:bg-zinc-700/40 rounded-md p-1.5">
                              <div className="text-[9px] font-semibold text-muted-foreground truncate mb-1">{name}</div>
                              {Array.from({ length: 2 + li }).map((_, ci) => (
                                <div key={ci} className="bg-card dark:bg-zinc-600/50 rounded h-3.5 mb-0.5 shadow-[0_0.5px_1px_rgba(0,0,0,0.06)]" />
                              ))}
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-t-xl">
                      <div className="flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-full backdrop-blur-sm text-white">
                        <Eye className="w-4 h-4" />
                        <span className="text-sm font-medium">Preview</span>
                      </div>
                    </div>
                  </div>

                  {/* Template Info */}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-heading font-semibold text-sm truncate">
                        {template.template_name || template.name}
                      </h3>
                      {template.category && (
                        <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded-full text-muted-foreground shrink-0 ml-2">
                          {template.category.name}
                        </span>
                      )}
                    </div>
                    {template.template_description && (
                      <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                        {template.template_description}
                      </p>
                    )}
                    
                    {/* Stats Row */}
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                      <span className="flex items-center gap-1" title="Lists">
                        <List className="w-3.5 h-3.5" />
                        {template.list_count || 0} lists
                      </span>
                      <span className="flex items-center gap-1" title="Cards">
                        <Square className="w-3.5 h-3.5" />
                        {template.card_count || 0} cards
                      </span>
                      {template.usage_count > 0 && (
                        <span className="flex items-center gap-1" title="Times used">
                          <Users className="w-3.5 h-3.5" />
                          {template.usage_count} uses
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <User className="w-4 h-4" />
                        <span>{template.creator?.name || 'Unknown'}</span>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {canManageTemplate(template) && (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={(e) => { e.stopPropagation(); handleEditTemplate(template); }}
                              data-testid={`edit-template-${template.board_id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template); }}
                              data-testid={`delete-template-${template.board_id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handleUseTemplate(template)}
                          className="bg-odapto-orange hover:bg-odapto-orange-hover text-white"
                          data-testid={`use-template-${template.board_id}`}
                        >
                          Use Template
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Template Preview Dialog — Board-like Read-Only */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-[95vw] w-full max-h-[92vh] h-[92vh] p-0 overflow-hidden gap-0 border-0 rounded-xl [&>button:last-child]:hidden" data-testid="template-preview-dialog">
          <DialogTitle className="sr-only">{previewTemplate?.template_name || 'Template Preview'}</DialogTitle>
          <DialogDescription className="sr-only">Read-only board preview</DialogDescription>
          {previewLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-10 h-10 border-4 border-odapto-orange border-t-transparent rounded-full animate-spin" />
            </div>
          ) : previewTemplate ? (
            <BoardPreview
              template={previewTemplate}
              onClose={() => setPreviewDialogOpen(false)}
              onUseTemplate={() => handleUseTemplate(previewTemplate)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Use Template Dialog */}
      <Dialog open={useDialogOpen} onOpenChange={setUseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Use Template</DialogTitle>
            <DialogDescription>
              Create a new board from "{selectedTemplate?.template_name}"
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={useTemplate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Select Workspace</Label>
              <Select value={selectedWorkspace} onValueChange={setSelectedWorkspace}>
                <SelectTrigger data-testid="workspace-select">
                  <SelectValue placeholder="Choose a workspace" />
                </SelectTrigger>
                <SelectContent>
                  {workspaces.map((ws) => (
                    <SelectItem key={ws.workspace_id} value={ws.workspace_id}>
                      {ws.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {workspaces.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  You need to create a workspace first.{' '}
                  <Link to="/dashboard" className="text-odapto-orange hover:underline">
                    Go to Dashboard
                  </Link>
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="board-name">Board Name</Label>
              <Input
                id="board-name"
                placeholder="My Board"
                value={newBoardName}
                onChange={(e) => setNewBoardName(e.target.value)}
                required
                data-testid="template-board-name-input"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setUseDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-odapto-orange hover:bg-odapto-orange-hover text-white"
                disabled={creating || !selectedWorkspace}
                data-testid="use-template-submit-btn"
              >
                {creating ? 'Creating...' : 'Create Board'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
            <DialogDescription>
              Update the details for "{editTemplate?.template_name}"
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveTemplateEdit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="edit-template-name">Template Name</Label>
              <Input
                id="edit-template-name"
                placeholder="Template name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
                data-testid="edit-template-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-template-desc">Description</Label>
              <Input
                id="edit-template-desc"
                placeholder="Template description"
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                data-testid="edit-template-desc-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={editCategory} onValueChange={setEditCategory}>
                <SelectTrigger data-testid="edit-template-category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-odapto-orange hover:bg-odapto-orange-hover text-white"
                disabled={saving || !editName.trim()}
                data-testid="edit-template-save-btn"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
